// @ts-strict-ignore
import type { AppDispatch, State } from '~/store';
import {
  createDataRoute, setLinkedExpressionItem, updateDependencies,
} from '../slice';
import { selectExpressionCaret } from '../selectors';
import { beginWorkflowHistoryTransaction } from './history';
import { getProperty } from 'dot-prop';
import { generatePublicId } from 'flow/service/publicIds';
import { getCreatedExpressionItemPath } from 'flow/editor/components/expressions/ExpressionBuilderDropdown/helpers';
import type { Expression } from '../../engine/types';

/** a path matching no item, which appends rather than going in at the front */
const APPEND = '-1';

type LinkTarget = { publicId: string; output?: { expression?: Expression } };

/** what a hover put into the expression, so leaving can take back exactly that */
export type LinkPreview = { dataRouteId: string; landedPath: string };

function linkAtCaret({
  dataRouteId,
  sourceId,
  targetAction,
  isPreview,
}: {
  dataRouteId: string;
  sourceId: string;
  targetAction: LinkTarget;
  isPreview: boolean;
}) {
  return (dispatch: AppDispatch, getState: () => State): string => {
    const caret = selectExpressionCaret(getState());
    const expression = targetAction.output?.expression ?? [];

    const caretPointsSomewhere = caret !== null
      && (caret.path === '' || getProperty(expression, caret.path) !== undefined);

    const placement = caretPointsSomewhere
      ? { path: caret.path, insertion: true, atGroupStart: caret.atGroupStart }
      : { path: APPEND, insertion: false, atGroupStart: false };

    const landedPath = getCreatedExpressionItemPath({
      existingExpression: expression,
      value: { data_route_id: dataRouteId },
      ...placement,
    });

    dispatch(createDataRoute({
      path: dataRouteId,
      value: { publicId: dataRouteId, sourceId, targetId: targetAction.publicId },
      expressionPath: placement.path,
      insertion: placement.insertion,
      atGroupStart: placement.atGroupStart,
      isPreview,
    }));

    return landedPath;
  };
}

/**
 * Shows what linking an action would add, leaving the caret where it is: the addition appears
 * beside it rather than taking the focus. Hands back what it added, so leaving takes exactly
 * that away and clicking keeps exactly it.
 */
export function startLinkPreviewThunk({
  sourceId,
  targetAction,
}: {
  sourceId: string;
  targetAction: LinkTarget;
}) {
  return (dispatch: AppDispatch): LinkPreview => {
    const dataRouteId = generatePublicId('dataroute');
    const landedPath = dispatch(linkAtCaret({
      dataRouteId, sourceId, targetAction, isPreview: true,
    }));

    return { dataRouteId, landedPath };
  };
}

/**
 * Keeps the link: the one a preview already added, or a new one when the click came without a
 * hover. Reports where it ended up, for the history entry to point undo at.
 */
export function keepLinkAtCaretThunk({
  sourceId,
  targetAction,
  preview,
  managesTransaction,
}: {
  sourceId: string;
  targetAction: LinkTarget;
  preview: LinkPreview | null;
  managesTransaction: boolean;
}) {
  return (dispatch: AppDispatch): string => {
    if (preview) {
      // the preview already put the route in, so only the dependency map is behind
      dispatch(updateDependencies());
    } else if (managesTransaction) {
      dispatch(beginWorkflowHistoryTransaction());
    }

    const landedPath = preview ? preview.landedPath : dispatch(linkAtCaret({
      dataRouteId: generatePublicId('dataroute'),
      sourceId,
      targetAction,
      isPreview: false,
    }));

    // the link is kept, so the caret goes to it and the next one follows it
    dispatch(setLinkedExpressionItem(landedPath));

    return landedPath;
  };
}
